import { useState } from "react";
import { motion } from "framer-motion";
import { SiteLayout } from "@/components/site/SiteLayout";
import { CTASection } from "@/components/site/CTASection";
import { faqCategories } from "@/data/faqContent";
import { SEOHead } from "@/components/SEOHead";
import { FAQSchema, BreadcrumbSchema } from "@/components/SchemaOrg";
import { FAQAnswer } from "@/components/site/FAQAnswer";
import miniRedbull from "@/assets/centre/mini-redbull-autosur.webp";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQPage = () => {
  const [activeCategory, setActiveCategory] = useState(faqCategories[0].id);

  const currentCategory = faqCategories.find(cat => cat.id === activeCategory);

  return (
    <SiteLayout>
      <SEOHead
        title="FAQ Contrôle Technique"
        description="Toutes les réponses à vos questions : tarifs, documents nécessaires, délais, contre-visite, validité. Préparez votre contrôle technique sereinement."
        path="/faq"
      />
      <FAQSchema items={faqCategories.flatMap(cat => cat.items)} />
      <BreadcrumbSchema items={[{ name: "Accueil", href: "/" }, { name: "FAQ", href: "/faq" }]} />
      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-autosur-navy to-autosur-blue overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${miniRedbull})` }}
          />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white">
              FAQ - CONSEILS
            </h1>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content - Two Column Layout */}
      <section className="py-12 md:py-16 bg-background">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            
            {/* Left Sidebar - Category Navigation */}
            <div className="lg:w-1/3 xl:w-1/4">
              <nav className="lg:sticky lg:top-24 space-y-2">
                {faqCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`block w-full text-left px-4 py-3 font-heading font-bold text-sm uppercase tracking-wide transition-colors border-l-4 ${
                      activeCategory === category.id
                        ? "text-autosur-red border-autosur-red bg-autosur-red/5"
                        : "text-autosur-navy border-transparent hover:text-autosur-blue hover:border-autosur-blue/30"
                    }`}
                  >
                    {category.title}
                  </button>
                ))}
              </nav>
            </div>

            {/* Right Content - Questions */}
            <div className="lg:w-2/3 xl:w-3/4">
              {currentCategory && (
                <motion.div
                  key={activeCategory}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Accordion type="single" collapsible className="space-y-0">
                    {currentCategory.items.map((item, index) => (
                      <AccordionItem
                        key={index}
                        value={`item-${index}`}
                        className="border-b border-gray-200 last:border-b-0"
                      >
                        <AccordionTrigger className="py-6 hover:no-underline group [&[data-state=open]>svg]:rotate-180">
                          <span className="font-heading font-bold text-autosur-navy text-left pr-4 group-hover:text-autosur-blue transition-colors">
                            {item.question}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="pb-6 pt-2">
                          <FAQAnswer answer={item.answer} question={item.question} />
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Vidéos FAQ Autosur */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-autosur-navy mb-2">
              La FAQ en vidéo
            </h2>
            <p className="font-body text-muted-foreground max-w-2xl mx-auto">
              Retrouvez les réponses aux questions les plus fréquentes en vidéo avec Autosur.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              { id: "qb-Mmalicfo", title: "AUTOSUR - FAQ du contrôle technique - #1" },
              { id: "x8CKEc4EcSg", title: "AUTOSUR - La FAQ du Contrôle technique - #2" },
              { id: "044up4tWqZ8", title: "AUTOSUR - La FAQ du Contrôle technique - #3" },
              { id: "RoXbY6mcsJw", title: "AUTOSUR - La FAQ du Contrôle Technique - #4" },
            ].map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="aspect-video rounded-2xl overflow-hidden shadow-xl">
                  <iframe
                    src={`https://www.youtube.com/embed/${video.id}`}
                    title={video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                    className="w-full h-full"
                  />
                </div>
                <p className="font-heading font-semibold text-autosur-navy text-sm mt-3 text-center">
                  {video.title}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection 
        title="Vous n'avez pas trouvé votre réponse ?"
        subtitle="Notre équipe est à votre disposition pour répondre à toutes vos questions sur le contrôle technique."
      />
    </SiteLayout>
  );
};

export default FAQPage;
